/**
 * Streama CMS · API types
 *
 * These types mirror the deployed Spring Boot backend's response shapes
 * exactly, as captured during the v2 validation runbook (Part A).
 *
 * The deployed backend uses two envelope shapes:
 *
 *   1. Success (controller-side):    { data, message }
 *   2. Failure (GlobalExceptionHandler): { status, error, message,
 *                                          fieldErrors?, timestamp }
 *
 * There is NO {ok: true/false, data, error} wrapper. Earlier docs
 * referenced one; the deployed backend never emitted it. The SPA now
 * matches the live contract.
 *
 * Types are also nullable-tolerant for fields the backend may omit
 * (e.g. fieldErrors only on validation failures, message only on
 * specific code paths).
 */

// -----------------------------------------------------------------
// Success envelope
// -----------------------------------------------------------------

/**
 * Wraps every successful (HTTP 2xx) response from the backend.
 *
 * `data` holds the typed payload. `message` is occasionally populated
 * for human-readable confirmations (e.g. "Login successful") and is
 * never required by the UI — we ignore it on success.
 */
export interface ApiSuccess<T> {
  data: T
  message?: string
}

// -----------------------------------------------------------------
// Error envelope (Spring's GlobalExceptionHandler default shape)
// -----------------------------------------------------------------

/**
 * Single field-level validation error from
 * MethodArgumentNotValidException. Maps a field name to the
 * constraint violation message.
 */
export interface FieldError {
  field: string
  message: string
}

/**
 * Backend error envelope, captured from the live deployment:
 *
 *   { "status": 400,
 *     "error":  "VALIDATION",
 *     "message": "Validation failed",
 *     "fieldErrors": [{"field": "userName", "message": "must not be blank"}, ...],
 *     "timestamp": "2025-..." }
 *
 *   { "status": 401,
 *     "error":  "INVALID_CREDENTIALS",
 *     "message": "Username or password is incorrect",
 *     "timestamp": "2025-..." }
 *
 * Spring Security's default 403 envelope from the JwtAuthenticationFilter
 * looks slightly different (no fieldErrors, error: "Forbidden"):
 *
 *   { "status": 403,
 *     "error":  "Forbidden",
 *     "path":   "/api/cms/...",
 *     "timestamp": "2025-..." }
 *
 * After the JwtAuthenticationEntryPoint backend patch lands, invalid
 * tokens will return 401 instead of 403 with the same standard
 * envelope shape — making client.ts's session-expiry detection
 * unambiguous. Until then, client.ts disambiguates using `error: "Forbidden"`
 * which only Spring's default emits.
 */
export interface ApiErrorEnvelope {
  status: number
  error: string
  message?: string
  fieldErrors?: FieldError[]
  /** Present on Spring Security default error envelopes (path that 403'd). */
  path?: string
  timestamp?: string
}

// -----------------------------------------------------------------
// Pagination
// -----------------------------------------------------------------

/**
 * Page wrapper for endpoints using Spring's Pageable. Backend
 * controllers serialize as a Map with these keys (verified across
 * Sprint 1-5 in SubscriberController.search, OfferController.list, etc).
 */
export interface Page<T> {
  content: T[]
  page: number
  size: number
  total: number
}

/**
 * Pagination request parameters. Matches Spring's Pageable URL
 * parameter names exactly.
 */
export interface PageRequest {
  page?: number
  size?: number
  /** "field,asc" or "field,desc" */
  sort?: string
}

// -----------------------------------------------------------------
// Auth domain
// -----------------------------------------------------------------

/**
 * The CMS admin user as represented in JWT claims and login responses.
 *
 * Privileges are stored as **bare names** (e.g. "videos", "check_users",
 * "offers") — NOT prefixed with "PRIV_". The PRIV_ prefix is an
 * internal Spring Security authority concern; on the wire and in the
 * SPA we use bare names. Router meta and useAuthority() consume bare
 * names directly.
 *
 * Shape matches the deployed backend's {@code IamDtos.UserSummary}
 * record exactly. {@code email} is not currently a column in the IAM
 * users table — the SPA never had a path to populate it. If the
 * backend later adds it, widen this type at that time.
 */
export interface CurrentUser {
  userName: string
  external: boolean
  privileges: string[]
}

/** Login request. POST /api/cms/users/login. */
export interface LoginRequest {
  userName: string
  password: string
}

/**
 * Login response payload (the `data` field of the wrapping ApiSuccess).
 *
 * Backend returns `expiresInSeconds` (e.g. 28800 = 8 hours). We
 * compute an absolute `expiresAt` ISO string in auth.ts at login time
 * for storage convenience — that derived field is added by the SPA,
 * not the backend.
 */
export interface LoginResponseRaw {
  token: string
  tokenType: string
  expiresInSeconds: number
  user: CurrentUser
}

/** SPA-side normalised login result with absolute `expiresAt`. */
export interface LoginResult {
  token: string
  expiresAt: string // ISO-8601, computed from expiresInSeconds
  user: CurrentUser
}

/**
 * Token refresh request — sent to POST /api/cms/users/refresh once
 * that endpoint is implemented (currently 404s; the SPA's poller
 * silently swallows the failure).
 */
export interface RefreshRequest {
  token: string
}

export type RefreshResponseRaw = LoginResponseRaw

// -----------------------------------------------------------------
// Exception type
// -----------------------------------------------------------------

/**
 * Custom exception thrown by the axios response interceptor when
 * the backend returns a non-2xx response. UI components catch this
 * to render error states with typed access to code, status, and
 * field-level validation errors.
 *
 * `code` mirrors the backend's `error` field when present (e.g.
 * "VALIDATION", "INVALID_CREDENTIALS"). For Spring Security default
 * envelopes that emit `error: "Forbidden"`, client.ts replaces with
 * a synthetic code so UI logic doesn't have to inspect strings.
 */
export class ApiException extends Error {
  public readonly code: string
  public readonly status: number
  public readonly details?: unknown
  public readonly fieldErrors: FieldError[]
  /** Lookup map for vee-validate-style "errors[fieldName]" patterns. */
  public readonly fieldErrorMap: Record<string, string>

  constructor(
    code: string,
    message: string,
    status: number,
    details?: unknown,
    fieldErrors: FieldError[] = [],
  ) {
    super(message)
    this.name = 'ApiException'
    this.code = code
    this.status = status
    this.details = details
    this.fieldErrors = fieldErrors
    this.fieldErrorMap = Object.fromEntries(
      fieldErrors.map((fe) => [fe.field, fe.message]),
    )
  }

  /**
   * Convenience: get the error message for a specific form field.
   * Returns undefined if the field has no error. Used by LoginView
   * and Sprint 2 form components for inline error rendering.
   */
  errorFor(field: string): string | undefined {
    return this.fieldErrorMap[field]
  }
}

// -----------------------------------------------------------------
// Synthetic error codes
// -----------------------------------------------------------------

/**
 * Codes the SPA produces when the backend doesn't supply one (or
 * supplies a non-machine-friendly string like "Forbidden"). UI
 * code can switch on these for control flow.
 */
export const ErrorCode = {
  VALIDATION: 'VALIDATION',
  INVALID_CREDENTIALS: 'INVALID_CREDENTIALS',
  /** Synthetic — issued for HTTP 401 when the backend is silent. */
  UNAUTHORIZED: 'UNAUTHORIZED',
  /** Synthetic — issued for HTTP 403 when the backend is silent. */
  FORBIDDEN: 'FORBIDDEN',
  /**
   * Synthetic — Spring Security default 403 detected via envelope
   * shape. The SPA treats this as session expiry rather than as a
   * privilege failure. Once the backend's JwtAuthenticationEntryPoint
   * is in place this case stops occurring.
   */
  SESSION_EXPIRED: 'SESSION_EXPIRED',
  NOT_FOUND: 'NOT_FOUND',
  CONFLICT: 'CONFLICT',
  INTERNAL: 'INTERNAL',
  NETWORK: 'NETWORK',
  UNKNOWN: 'UNKNOWN',
} as const

export type ErrorCodeValue = (typeof ErrorCode)[keyof typeof ErrorCode]
